import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route, Router } from '@angular/router';
import { EMPTY, Observable, finalize } from 'rxjs';
import { findPath } from './utils/find-full-path';
import { FluxRouteData } from '../types';
import { fluxLogger } from '@core/services/logger/logger.instance';

const logger = fluxLogger.extend('router').extend('preload');

@Injectable({ providedIn: 'root' })
export class FluxPreloadStrategy extends PreloadingStrategy {
  private loadingRoutes = new Set<Route>();

  public constructor(private readonly router: Router) {
    super();
  }

  override preload(route: Route, loadMe: () => Observable<any>): Observable<any> {
    const routeData = route.data as FluxRouteData | undefined;
    const currentRoutePath = findPath(this.router.config, route);

    if (routeData && routeData.preload === false) {
      return EMPTY;
    }

    logger.debug('Preloading route.', {
      route,
      currentRoutePath,
    });

    if (!routeData?.preloadWhen) {
      logger.debug('Route does not have preloadWhen, skipping preload.', {
        route,
        currentRoutePath,
      });
      return loadMe();
    }

    return this.handleRoutePreload(route, currentRoutePath, loadMe);
  }

  private handleRoutePreload(
    route: Route,
    currentRoutePath: string,
    loadMe: () => Observable<any>,
  ): Observable<any> {
    const currentPath = this.router.url.split('?')[0];
    const routeData = route.data as FluxRouteData | undefined;

    if (!currentRoutePath) {
      logger.debug('Route does not have a path, skipping preload.', {
        route,
        currentRoutePath,
      });
      return EMPTY;
    }

    if (routeData?.preloadWhen) {
      const conditions = routeData.preloadWhen;
      let canPreload = true;

      if (conditions.matches) {
        canPreload = canPreload && conditions.matches.test(currentPath);
      }
      if (conditions.startsWith) {
        canPreload = canPreload && currentPath.startsWith(conditions.startsWith);
      }
      if (conditions.endsWith) {
        canPreload = canPreload && currentPath.endsWith(conditions.endsWith);
      }

      if (!canPreload) {
        logger.debug('Route does not match preload conditions, skipping preload.', {
          route,
          currentRoutePath,
        });
        return EMPTY;
      }

      if (!this.loadingRoutes.has(route)) {
        this.loadingRoutes.add(route);
        logger.info('Preloading route.', {
          route,
          currentRoutePath,
        });
        return loadMe().pipe(
          finalize(() => {
            this.loadingRoutes.delete(route);
          }),
        );
      }

      logger.debug('Route is already being preloaded, skipping preload.', {
        route,
        currentRoutePath,
      });
      return EMPTY;
    }

    logger.debug('Route does not match preload conditions, skipping preload.', {
      route,
      currentRoutePath,
    });

    return EMPTY;
  }
}
